import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Users,
  Bell,
  Shield,
  FileText,
  User,
  Plus,
  Check,
  X
} from 'lucide-react';

// Mock data - will be replaced with API calls
const mockRoles = [
  {
    id: '1',
    name: 'Super Admin',
    permissions: {
      manageUsers: true,
      approveEvents: true,
      moderatePosts: true,
      viewAnalytics: true,
      settingsAccess: true
    }
  },
  {
    id: '2',
    name: 'Moderator',
    permissions: {
      manageUsers: false,
      approveEvents: false,
      moderatePosts: true,
      viewAnalytics: false,
      settingsAccess: false
    }
  },
  {
    id: '3',
    name: 'Event Manager',
    permissions: {
      manageUsers: false,
      approveEvents: true,
      moderatePosts: false,
      viewAnalytics: true,
      settingsAccess: false
    }
  },
  {
    id: '4',
    name: 'Community Manager',
    permissions: {
      manageUsers: false,
      approveEvents: false,
      moderatePosts: true,
      viewAnalytics: true,
      settingsAccess: false
    }
  }
];

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'roles' | 'notifications' | 'security' | 'logs' | 'account'>('general');
  const [roles, setRoles] = useState(mockRoles);
  const [loading, setLoading] = useState(false);

  const handlePermissionToggle = async (roleId: string, permission: string) => {
    setRoles(prev => prev.map(role => {
      if (role.id === roleId) {
        return {
          ...role,
          permissions: {
            ...role.permissions,
            [permission]: !role.permissions[permission as keyof typeof role.permissions]
          }
        };
      }
      return role;
    }));
    // API call would go here
  };

  const handleAddAdmin = () => {
    console.log('Add admin');
    // Open modal or form
  };

  const PermissionCheckbox: React.FC<{
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
  }> = ({ checked, onChange, disabled = false }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
        checked
          ? 'bg-gray-900 border-gray-900 text-white'
          : 'border-gray-300 hover:border-gray-400'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {checked && <Check className="w-3 h-3" />}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            Admin Portal v.2.1.0
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'general', label: 'General', icon: SettingsIcon },
            { key: 'roles', label: 'Roles', icon: Users },
            { key: 'notifications', label: 'Notifications', icon: Bell },
            { key: 'security', label: 'Security', icon: Shield },
            { key: 'logs', label: 'System Logs', icon: FileText },
            { key: 'account', label: 'Account', icon: User }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeTab === 'general' && (
          <div className="p-6">
            <div className="text-center py-12">
              <SettingsIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">General Settings</h3>
              <p className="text-gray-500">General platform settings will be available here.</p>
            </div>
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Role Permissions</h3>
              <button
                onClick={handleAddAdmin}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Admin
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Manage Users
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approve Events
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Moderate Posts
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      View Analytics
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Settings Access
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {roles.map((role) => (
                    <tr key={role.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <PermissionCheckbox
                          checked={role.permissions.manageUsers}
                          onChange={() => handlePermissionToggle(role.id, 'manageUsers')}
                          disabled={role.name === 'Super Admin'}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <PermissionCheckbox
                          checked={role.permissions.approveEvents}
                          onChange={() => handlePermissionToggle(role.id, 'approveEvents')}
                          disabled={role.name === 'Super Admin'}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <PermissionCheckbox
                          checked={role.permissions.moderatePosts}
                          onChange={() => handlePermissionToggle(role.id, 'moderatePosts')}
                          disabled={role.name === 'Super Admin'}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <PermissionCheckbox
                          checked={role.permissions.viewAnalytics}
                          onChange={() => handlePermissionToggle(role.id, 'viewAnalytics')}
                          disabled={role.name === 'Super Admin'}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <PermissionCheckbox
                          checked={role.permissions.settingsAccess}
                          onChange={() => handlePermissionToggle(role.id, 'settingsAccess')}
                          disabled={role.name === 'Super Admin'}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="p-6">
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Notification Settings</h3>
              <p className="text-gray-500">Notification preferences and settings will be available here.</p>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="p-6">
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Security Settings</h3>
              <p className="text-gray-500">Security configurations and access controls will be available here.</p>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="p-6">
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">System Logs</h3>
              <p className="text-gray-500">System activity logs and audit trails will be displayed here.</p>
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="p-6">
            <div className="text-center py-12">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Account Settings</h3>
              <p className="text-gray-500">Account management and profile settings will be available here.</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-4 text-sm text-gray-500">
        © Sportofolo 2025 • Version 2.1.0
      </div>
    </div>
  );
};

export default AdminSettings;